import React, { useState, useEffect, useRef } from 'react';
import { AnimatePresence } from 'motion/react';
import * as motion from 'motion/react-m';
import { faCheck } from '@fortawesome/pro-regular-svg-icons/faCheck';
import { faCircle } from '@fortawesome/pro-regular-svg-icons/faCircle';
import { faCircleDot } from '@fortawesome/pro-regular-svg-icons/faCircleDot';

import { useQuizContext } from '../../../../context/QuizContext';
import QuizSubmitButtons from '../../QuizSubmitButtons';
import {
  EliminationToolIconButtons,
  ExplanationDropDown,
  ExplanationMessage,
} from '../utils';
import { getOptionColor, optionStateEnum } from '../../../../constants/quiz';
import { Flex } from '@osmosis/react-ui/components/Box';
import { Box } from '@osmosis/react-ui/components/Box';
import { Icon } from '@osmosis/react-ui/components/Icon';
import { useColors } from '@osmosis/react-ui/hooks/useColors';
import { P } from '@osmosis/react-ui/components/Text';
import { expand } from '../../../../styles/animation';
import { clickable } from '../../../../styles/hover';
import { QuizConfidence, QuizExplanationObject } from '../../../../types/quiz';
import { useIntl } from 'react-intl';

type Props = {
  text: string;
  isCorrect: boolean;
  explanation: string | QuizExplanationObject;
  checkedOption: number;
  index: number;
  setCheckedOption: (value: number) => void;
};

const circleIconProps = { height: 20, width: 20 };

/**
 * Radio option for AnswerChoice component.
 */
const AnswerChoiceOption = ({
  text,
  isCorrect,
  explanation,
  checkedOption,
  index,
  setCheckedOption,
}: Props) => {
  const {
    isAnswered,
    showElimination,
    setEliminationItems,
    submitAnswer,
    setShowLab,
  } = useQuizContext();
  const [optionState, setOptionState] = useState(optionStateEnum.default);
  const [eliminatedAnswer, setEliminatedAnswer] = useState(false);
  const [explanationVisible, setExplanationVisible] = useState(false);
  const colors = useColors();
  const optionRef = useRef<HTMLDivElement>(null);
  const intl = useIntl();
  useEffect(() => {
    if (checkedOption === index) {
      setOptionState(optionStateEnum.chosen);
    }
    if (checkedOption !== index) {
      setOptionState(optionStateEnum.default);
    }
    if (eliminatedAnswer) {
      setOptionState(optionStateEnum.eliminated);
    }
    if (isCorrect && isAnswered) {
      setOptionState(optionStateEnum.correct);
    }
    if (checkedOption === index && !isCorrect && isAnswered) {
      setOptionState(optionStateEnum.incorrect);
    }
  }, [isAnswered, isCorrect, checkedOption, index, eliminatedAnswer]);

  useEffect(() => {
    setEliminatedAnswer(false);
    setEliminationItems([]);
  }, [showElimination, setEliminationItems]);

  const isCorrectAnswer = optionState === optionStateEnum.correct;
  const isChosen = optionState === optionStateEnum.chosen;

  const handleClick = () => {
    if (!isAnswered) setCheckedOption(index);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === ' ' || event.key === 'Enter') {
      handleClick();
      event.preventDefault();
    } else if (event.key === 'ArrowUp') {
      focusPreviousOption();
      event.preventDefault();
    } else if (event.key === 'ArrowDown') {
      focusNextOption();
      event.preventDefault();
    }
  };

  const focusPreviousOption = () => {
    if (optionRef.current) {
      const previousOption = optionRef.current
        .previousElementSibling as HTMLDivElement;
      if (previousOption) {
        previousOption.focus();
      }
    }
  };

  const focusNextOption = () => {
    if (optionRef.current) {
      const nextOption = optionRef.current.nextElementSibling as HTMLDivElement;
      if (nextOption) {
        nextOption.focus();
      }
    }
  };

  const submitQuizButtons = (confidence: QuizConfidence) => {
    setShowLab(false);
    submitAnswer(confidence, isCorrect, checkedOption);
  };

  return (
    <Flex
      ref={optionRef}
      tabIndex={0}
      role="radio"
      aria-checked={checkedOption === index}
      mb={2}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      data-testid="answer-choice-option"
      width="100%"
    >
      <Box mt={3} mr={3} ml={3} sx={{ position: 'relative' }}>
        {isCorrectAnswer && (
          <Box
            sx={{ position: 'absolute', left: '-20px' }}
            aria-label={intl.formatMessage({
              id: 'correct_answer',
              defaultMessage: 'Correct answer:',
            })}
          >
            <Icon icon={faCheck} {...circleIconProps} color={colors.positive} />
          </Box>
        )}

        {checkedOption === index ? (
          <Icon
            icon={faCircleDot}
            {...circleIconProps}
            color={colors.primary}
          />
        ) : (
          <Icon icon={faCircle} {...circleIconProps} color={colors.gray40} />
        )}
      </Box>
      <Flex alignItems="flex-start" flexDirection="column" width="100%">
        <Flex
          flexDirection="column"
          py="18px"
          px="14px"
          bg={getOptionColor(optionState).bg}
          width="100%"
          sx={{
            border: '1px solid',
            borderColor: getOptionColor(optionState).borderColor,
            borderRadius: 5,
            ...(!isAnswered && clickable('bg-1', 'bg-2')),
            gap: 2,
          }}
        >
          <Flex justifyContent="space-between" alignItems="center" width="100%">
            <Flex sx={{ color: 'gray60' }}>
              <P
                pl="10px"
                color="text"
                m={0}
                sx={{
                  color: getOptionColor(optionState).color,
                  textDecoration: getOptionColor(optionState).textDecoration,
                }}
                display="inline-block"
                dangerouslySetInnerHTML={{
                  __html: text,
                }}
              />
            </Flex>
            <EliminationToolIconButtons
              index={index}
              setEliminatedAnswer={setEliminatedAnswer}
              eliminatedAnswer={eliminatedAnswer}
            />
          </Flex>
          <AnimatePresence>
            {isChosen && (
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              <motion.div {...(expand() as any)}>
                <QuizSubmitButtons
                  submit={(confidence) => {
                    if (checkedOption > -1) {
                      submitQuizButtons(confidence);
                    }
                  }}
                />
              </motion.div>
            )}
          </AnimatePresence>
          {isAnswered && !explanationVisible && explanation && (
            <ExplanationDropDown
              setExplanationVisible={setExplanationVisible}
              isOpen={explanationVisible}
            />
          )}
        </Flex>
        {explanationVisible && explanation && (
          <ExplanationMessage explanation={explanation} />
        )}
      </Flex>
    </Flex>
  );
};

export default AnswerChoiceOption;
